// Mock Recipes Data
// Extracted from MealPlanner.tsx and RecipeBook.tsx

export interface RecipeIngredient {
  id: string;
  name: string;
  amount: number;
  unit: string;
  fdc_id?: number; // Link to USDA database
  ndc_code?: string; // For supplements/medications
  notes?: string;
  substitutions?: string[];
}

export interface Recipe {
  id: string;
  name: string;
  description: string;
  servings: number;
  prep_time: number;
  cook_time: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  cuisine_type: string;
  dietary_tags: string[];
  ingredients: RecipeIngredient[];
  instructions: string[];
  nutrition_per_serving: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
  };
  source: 'guru' | 'personal' | 'public_domain' | 'community';
  source_details: {
    guru_name?: string;
    book_title?: string;
    contributed_by?: string;
    scan_source?: string;
  };
  image_url?: string;
  rating: number;
  save_count: number;
  created_at: string;
  updated_at: string;
}

export const mockRecipes: Recipe[] = [
  {
    id: '1',
    name: 'Mediterranean Quinoa Bowl',
    description: 'Nutrient-rich bowl with quinoa, vegetables, and olive oil',
    servings: 4,
    prep_time: 15,
    cook_time: 20,
    difficulty: 'Easy',
    cuisine_type: 'Mediterranean',
    dietary_tags: ['vegetarian', 'gluten-free', 'high-protein'],
    ingredients: [
      {
        id: '1',
        name: 'Quinoa',
        amount: 1,
        unit: 'cup',
        fdc_id: 168917,
        substitutions: ['brown rice', 'bulgur wheat']
      },
      {
        id: '2',
        name: 'Cherry Tomatoes',
        amount: 2,
        unit: 'cups',
        fdc_id: 170457
      },
      {
        id: '3',
        name: 'Cucumber',
        amount: 1,
        unit: 'medium',
        fdc_id: 168463
      },
      {
        id: '4',
        name: 'Olive Oil',
        amount: 2,
        unit: 'tbsp',
        fdc_id: 171413
      }
    ],
    instructions: [
      'Rinse quinoa and cook according to package directions',
      'Chop vegetables while quinoa cooks',
      'Combine all ingredients and dress with olive oil'
    ],
    nutrition_per_serving: {
      calories: 320,
      protein: 12,
      carbs: 45,
      fat: 8,
      fiber: 6
    },
    source: 'guru',
    source_details: {
      guru_name: 'Dr. Sarah Mediterranean'
    },
    rating: 4.8,
    save_count: 1247,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Grilled Chicken Salad',
    description: 'Fresh greens with grilled chicken and vinaigrette',
    servings: 2,
    prep_time: 10,
    cook_time: 15,
    difficulty: 'Easy',
    cuisine_type: 'American',
    dietary_tags: ['high-protein', 'low-carb'],
    ingredients: [
      {
        id: '5',
        name: 'Chicken Breast',
        amount: 2,
        unit: 'pieces',
        fdc_id: 171686
      },
      {
        id: '6',
        name: 'Mixed Greens',
        amount: 4,
        unit: 'cups',
        fdc_id: 169248
      },
      {
        id: '7',
        name: 'Olive Oil',
        amount: 1,
        unit: 'tbsp',
        fdc_id: 171413
      }
    ],
    instructions: [
      'Season chicken with herbs and spices',
      'Grill chicken until cooked through',
      'Toss greens with vinaigrette and top with sliced chicken'
    ],
    nutrition_per_serving: {
      calories: 280,
      protein: 25,
      carbs: 12,
      fat: 15,
      fiber: 4
    },
    source: 'personal',
    source_details: {
      contributed_by: 'Your Recipe'
    },
    rating: 4.5,
    save_count: 892,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: '3',
    name: 'Overnight Oats with Berries',
    description: 'Healthy breakfast prepared the night before',
    servings: 1,
    prep_time: 5,
    cook_time: 0,
    difficulty: 'Easy',
    cuisine_type: 'American',
    dietary_tags: ['vegetarian', 'gluten-free', 'high-fiber'],
    ingredients: [
      {
        id: '8',
        name: 'Rolled Oats',
        amount: 0.5,
        unit: 'cup',
        fdc_id: 168917
      },
      {
        id: '9',
        name: 'Almond Milk',
        amount: 0.5,
        unit: 'cup',
        fdc_id: 171415
      },
      {
        id: '10',
        name: 'Mixed Berries',
        amount: 0.5,
        unit: 'cup',
        fdc_id: 173946
      }
    ],
    instructions: [
      'Mix oats with almond milk in a jar',
      'Add berries and refrigerate overnight',
      'Enjoy cold or warm in the morning'
    ],
    nutrition_per_serving: {
      calories: 250,
      protein: 8,
      carbs: 45,
      fat: 5,
      fiber: 8
    },
    source: 'community',
    source_details: {
      contributed_by: 'Community Recipe'
    },
    rating: 4.6,
    save_count: 567,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: '4',
    name: 'Salmon with Roasted Vegetables',
    description: 'Omega-3 rich salmon with colorful roasted vegetables',
    servings: 2,
    prep_time: 15,
    cook_time: 25,
    difficulty: 'Medium',
    cuisine_type: 'Mediterranean',
    dietary_tags: ['high-protein', 'omega-3', 'anti-inflammatory'],
    ingredients: [
      {
        id: '11',
        name: 'Salmon Fillet',
        amount: 2,
        unit: 'pieces',
        fdc_id: 173686
      },
      {
        id: '12',
        name: 'Broccoli',
        amount: 2,
        unit: 'cups',
        fdc_id: 170379
      },
      {
        id: '13',
        name: 'Sweet Potato',
        amount: 1,
        unit: 'medium',
        fdc_id: 168482
      }
    ],
    instructions: [
      'Preheat oven to 400°F',
      'Season salmon with herbs and lemon',
      'Roast vegetables until tender',
      'Bake salmon for 12-15 minutes'
    ],
    nutrition_per_serving: {
      calories: 380,
      protein: 35,
      carbs: 25,
      fat: 18,
      fiber: 6
    },
    source: 'guru',
    source_details: {
      guru_name: 'Coach Mike Rodriguez'
    },
    rating: 4.7,
    save_count: 734,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: '5',
    name: 'Smoothie Bowl',
    description: 'Colorful smoothie bowl topped with granola and fruits',
    servings: 1,
    prep_time: 10,
    cook_time: 0,
    difficulty: 'Easy',
    cuisine_type: 'American',
    dietary_tags: ['vegetarian', 'gluten-free', 'antioxidant-rich'],
    ingredients: [
      {
        id: '14',
        name: 'Frozen Berries',
        amount: 1,
        unit: 'cup',
        fdc_id: 173946
      },
      {
        id: '15',
        name: 'Banana',
        amount: 1,
        unit: 'medium',
        fdc_id: 173944
      },
      {
        id: '16',
        name: 'Greek Yogurt',
        amount: 0.5,
        unit: 'cup',
        fdc_id: 171415
      }
    ],
    instructions: [
      'Blend frozen berries, banana, and yogurt',
      'Pour into a bowl',
      'Top with granola, fresh fruits, and seeds'
    ],
    nutrition_per_serving: {
      calories: 320,
      protein: 15,
      carbs: 55,
      fat: 8,
      fiber: 7
    },
    source: 'personal',
    source_details: {
      contributed_by: 'Your Recipe'
    },
    rating: 4.4,
    save_count: 423,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
]; 